import React, { useState, useEffect } from 'react';
import {
    Button, Table, Modal, Form, Container, Row, Col,
    Alert, Badge, Spinner
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaShieldAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BASE_URL } from '../../Constants/constants';

// Level labels shown in the UI
const LEVEL_OPTIONS = [
    { value: 0, label: '0 — Guest (no privileges)' },
    { value: 1, label: '1 — Staff (standard user)' },
    { value: 2, label: '2 — Department (ICT / HR / Security / Finance)' },
    { value: 3, label: '3 — Management' },
    { value: 4, label: '4 — Admin (full access)' },
];

const getLevelBadgeVariant = (level) => {
    if (level >= 4) return 'danger';
    if (level === 3) return 'warning';
    if (level === 2) return 'primary';
    if (level === 1) return 'secondary';
    return 'light';
};

const RoleManagement = () => {
    const { t } = useTranslation('roles');

    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleName, setRoleName] = useState('');
    const [roleDescription, setRoleDescription] = useState('');
    const [roleLevel, setRoleLevel] = useState(1);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        fetchRoles();
    }, []);

    // Auto-clear success messages after 4 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/roles/`);
            setRoles(response.data);
        } catch (err) {
            setError(extractError(err));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Extract a meaningful error message from an Axios error.
     * Shows the backend `detail` field if available, otherwise a generic message.
     */
    const extractError = (err) => {
        return err.response?.data?.detail || t('roles.messages.error');
    };

    const handleCreateOrUpdateRole = async () => {
        setError(null);

        if (!roleName.trim()) {
            setError('Role name is required.');
            return;
        }

        try {
            const payload = {
                name: roleName.trim().toUpperCase(),
                description: roleDescription.trim() || null,
                level: roleLevel,
            };

            if (selectedRole) {
                await axios.put(`${BASE_URL}/roles/${selectedRole.id}`, payload);
                setSuccess(t('roles.messages.roleUpdated'));
            } else {
                await axios.post(`${BASE_URL}/roles/`, payload);
                setSuccess(t('roles.messages.roleCreated'));
            }

            closeModal();
            fetchRoles();
        } catch (err) {
            setError(extractError(err));
        }
    };

    const handleDeleteRole = async (role) => {
        if (!window.confirm(
            `Are you sure you want to delete the role "${role.name}"?\n\n` +
            `This will fail if any users are currently assigned to it.`
        )) return;

        setError(null);
        try {
            await axios.delete(`${BASE_URL}/roles/${role.id}`);
            setSuccess(t('roles.messages.roleDeleted'));
            fetchRoles();
        } catch (err) {
            // Shows backend message e.g. "X users are assigned to this role"
            setError(extractError(err));
        }
    };

    const openModalForEdit = (role) => {
        setSelectedRole(role);
        setRoleName(role.name);
        setRoleDescription(role.description || '');
        setRoleLevel(role.level ?? 1);
        setError(null);
        setShowModal(true);
    };

    const openModalForCreate = () => {
        setSelectedRole(null);
        setRoleName('');
        setRoleDescription('');
        setRoleLevel(1);
        setError(null);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedRole(null);
        setRoleName('');
        setRoleDescription('');
        setRoleLevel(1);
        setError(null);
    };

    return (
        <Container className="mt-5">
            <Row>
                <Col className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3>{t('roles.title')}</h3>
                        <p className="text-muted">{t('roles.description')}</p>
                    </div>
                    <Button variant="success" onClick={openModalForCreate}>
                        <FaPlus className="me-1" />
                        {t('roles.buttons.addRole')}
                    </Button>
                </Col>
            </Row>

            {/* Global alerts */}
            {error && (
                <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
                    <strong>Error:</strong> {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" className="mt-3" dismissible onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Roles table */}
            {loading ? (
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <Table bordered hover responsive className="mt-3">
                    <thead className="table-dark">
                        <tr>
                            <th>#</th>
                            <th>{t('roles.table.roleName')}</th>
                            <th>Privilege Level</th>
                            <th>{t('roles.table.description')}</th>
                            <th>{t('roles.table.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roles.length > 0 ? (
                            roles.map((role, index) => (
                                <tr key={role.id}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <Badge bg="secondary">{role.name}</Badge>
                                    </td>
                                    <td>
                                        <Badge bg={getLevelBadgeVariant(role.level)}>
                                            <FaShieldAlt className="me-1" />
                                            Level {role.level}
                                        </Badge>
                                    </td>
                                    <td>{role.description || <span className="text-muted">—</span>}</td>
                                    <td>
                                        <Button
                                            variant="warning"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => openModalForEdit(role)}
                                        >
                                            <FaEdit className="me-1" />
                                            {t('roles.buttons.edit')}
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleDeleteRole(role)}
                                        >
                                            <FaTrash className="me-1" />
                                            {t('roles.buttons.delete')}
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center text-muted py-4">
                                    No roles found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            )}

            {/* Create / Edit Modal */}
            <Modal show={showModal} onHide={closeModal} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedRole ? t('roles.buttons.edit') : t('roles.buttons.addRole')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            <strong>Error:</strong> {error}
                        </Alert>
                    )}
                    <Form>
                        {/* Role Name */}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                {t('roles.form.roleName')} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={t('roles.form.roleNamePlaceholder')}
                                value={roleName}
                                onChange={(e) => setRoleName(e.target.value.toUpperCase())}
                            />
                            <Form.Text className="text-muted">
                                e.g. FINANCE, LOGISTICS, COMMS — any name is accepted.
                            </Form.Text>
                        </Form.Group>

                        {/* Privilege Level */}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Privilege Level <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={roleLevel}
                                onChange={(e) => setRoleLevel(parseInt(e.target.value))}
                            >
                                {LEVEL_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Controls what this role can do. Users can only assign roles at or
                                below their own level.
                            </Form.Text>
                        </Form.Group>

                        {/* Description */}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('roles.form.description')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder={t('roles.form.descriptionPlaceholder')}
                                value={roleDescription}
                                onChange={(e) => setRoleDescription(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal}>
                        <FaTimes className="me-1" />
                        {t('roles.buttons.cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleCreateOrUpdateRole}>
                        <FaSave className="me-1" />
                        {t('roles.buttons.save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default RoleManagement;
